import type { ComponentType, ReactNode } from 'react'

/**
 * Lazy page map. This used to be `{ eager: true }`, which pulled all 137 pages
 * into a single 3.3 MB chunk that every visitor downloaded — including
 * anonymous buyers on a customer's trust center, who need 14 of them.
 *
 * Without `eager`, each value is a `() => Promise<Module>` and Vite emits one
 * chunk per page, loaded on demand. Inertia's `resolve` accepts a promise, so
 * the only change on the call side is that this function is now async.
 */
const pages = import.meta.glob('../pages/**/*.tsx') as Record<
  string,
  () => Promise<{ default: ComponentType<any> }>
>

/**
 * Layouts are lazy too, and each one imports its own surface's stylesheet
 * bundle (styles/bundles/*). That is what keeps marketing CSS out of the app
 * and the admin app's CSS off a customer's public trust center — Vite emits
 * the CSS alongside the layout chunk that imports it.
 */
const layouts = {
  help:      () => import('@/layouts/HelpLayout'),
  admin:     () => import('@/layouts/AdminLayout'),
  adminAuth: () => import('@/layouts/AdminAuthLayout'),
  app:       () => import('@/layouts/AppLayout'),
  public:    () => import('@/layouts/PublicLayout'),
  auth:      () => import('@/layouts/AuthLayout'),
  marketing: () => import('@/layouts/MarketingLayout'),
} as const

function layoutKey(name: string): keyof typeof layouts | undefined {
  if (name.startsWith('Help/')) return 'help'
  if (name.startsWith('App/')) return 'app'
  if (name.startsWith('TrustCenter/')) return 'public'
  if (name.startsWith('Auth/')) return 'auth'
  if (name.startsWith('Marketing/')) return 'marketing'
  // Staff console. Its sign-in surfaces carry no chrome (there is no session
  // yet and no nav to show), so they get their own bare dark layout.
  if (name.startsWith('Admin/Auth/')) return 'adminAuth'
  if (name.startsWith('Admin/')) return 'admin'
  return undefined
}

export async function resolvePageComponent(name: string) {
  const path = `../pages/${name}.tsx`
  const importer = pages[path]

  if (!importer) {
    throw new Error(`Page not found: ${name} (looked for ${path})`)
  }

  const key = layoutKey(name)
  // Page and layout load in parallel — the layout chunk is shared across every
  // page on the surface, so after the first navigation it is already cached.
  const [page, layoutMod] = await Promise.all([
    importer(),
    key ? layouts[key]() : Promise.resolve(undefined),
  ])

  const component = page.default

  if (layoutMod && !(component as any).layout) {
    const Layout = layoutMod.default
    ;(component as any).layout = (page: ReactNode) => <Layout>{page}</Layout>
  }

  return component
}
